import React, { useEffect } from 'react';
import { XCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import useClickOutside from '../../hooks/useClickOutside';

interface ErrorModalProps {
  message: string;
  onClose: () => void;
  autoClose?: boolean;
  autoCloseDelay?: number;
  title?: string;
}

const ErrorModal: React.FC<ErrorModalProps> = ({
  message,
  onClose,
  autoClose = false,
  autoCloseDelay = 5000,
  title = 'Error!'
}) => {
  const modalRef = useClickOutside(onClose);
  useEffect(() => {
    if (autoClose) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);

      return () => clearTimeout(timer);
    }
  }, [autoClose, autoCloseDelay, onClose]);

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div ref={modalRef} className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="mt-3 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <XCircleIcon className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            {title}
          </h3>
          <div className="mt-2 px-7 py-3">
            <div className="text-sm text-gray-600 text-left">
              {message.split('\n').map((line, index) => (
                <div key={index} className="mb-1">
                  {line.startsWith('• ') ? (
                    <div className="ml-4">
                      <span className="text-red-600 font-medium">•</span>
                      <span className="ml-2">{line.substring(2)}</span>
                    </div>
                  ) : line.startsWith('Suggestions:') || line.startsWith('To delete this category:') ? (
                    <div className="mt-3 mb-2 font-medium text-gray-700 border-t pt-2">
                      💡 {line}
                    </div>
                  ) : line.trim() === '' ? (
                    <div className="h-2"></div>
                  ) : (
                    <div className={line.includes('Cannot delete') ? 'font-medium text-gray-800' : ''}>
                      {line}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          {autoClose && (
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-1">
                <div 
                  className="bg-red-600 h-1 rounded-full"
                  style={{
                    animation: `shrinkProgress ${autoCloseDelay}ms linear forwards`
                  }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                This message will close automatically
              </p>
            </div>
          )}
          <div className="flex justify-center mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              OK
            </button>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes shrinkProgress {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
      `}</style>
    </div>
  );
};

export default ErrorModal;
